import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

// components
import DeleteSelected from "./DeleteSelected";
import DeletePopup from "./DeletePopup";
import EditSelected from "./EditSelected";
import ErrorDisplay from "./ErrorDisplay";
import UpdateRequired from "./UpdateRequired";
import DropDown from "../formComponents/DropDown";
import Loading from "./Loading";
import DownloadCsv from "./DownloadCsv";

// components

// data
import { collectionData } from "../data/collections-data";

// styles and images
import "./Components.css";
import Add from "../assets/add.svg";
import Merge from "../assets/merge.svg";
import Login from "../assets/login.svg";
import DownloadFscTemplate from "../assets/download.svg";

// store
import { messageAndErrorStore, triggersStore, userStore } from "../store/Store";

// pages components
import AddFuelSurcharge from "../pages/fuel-surcharge/AddFuelSurcharge";
import AddAssetRateSheet from "../pages/asset-rate-sheets/AddAssetRateSheet";
import AddEditAdminCompany from "../pages/admin/AddEditAdminCompany";
import AddCompany from "../pages/companies/AddCompany";
import AddRateSheet from "../pages/rate-sheets/AddRateSheet";
import AddEditCompanyCarrier from "../pages/company-carrier-drivers/AddEditCompanyCarrier";
import AddEditCarrierDriver from "../pages/company-carrier-drivers/AddEditCarrierDriver";
import AddTieredRateSheet from "../pages/rate-sheets/AddTieredRateSheet";
import AddCompanyList from "../pages/company-settings/AddCompanyList";
import MergeCompanies from "../pages/admin/MergeCompanies";
import AddEditCompanyDriver from "../pages/company-drivers/AddEditCompanyDriver";
import CreateFscTemplate from "../pages/fuel-surcharge/CreateFscTemplate";
import { getLocalDateString } from "../funcs/helperFuncs";

export default function DisplayList({
  coll,
  docs,
  err,
  trigger,
  setTrigger,
  url,
  setState,
  statusToggle,
  isNavigation,
  collectionName,
  updateRequiredField,
  updateRequiredValue,
  doNotDisplayTitle,
  setDeleteState,
  setDeleteStates,
  setDeleteStateFields,
}) {
  // global state
  const JwtToken = userStore((userDataStore) => userDataStore.JwtToken);
  const setJwtToken = userStore((userDataStore) => userDataStore.setJwtToken);
  const setCurrentUser = userStore(
    (userDataStore) => userDataStore.setCurrentUser,
  );
  const setCurrentCompany = userStore(
    (userDataStore) => userDataStore.setCurrentCompany,
  );
  const currentUser = userStore((userDataStore) => userDataStore.currentUser);
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const setAuthError = messageAndErrorStore((data) => data.setAuthError);
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)
  const companyMergeTrigger = triggersStore((data) => data.companyMergeTrigger);
  const setCompanyMergeTrigger = triggersStore(
    (data) => data.setCompanyMergeTrigger,
  );

  // state
  const navigate = useNavigate();
  const [deleteIsTriggered, setDeleteIsTriggered] = useState(false);
  const [error, setError] = useState(null);
  const [docId, setDocId] = useState("");
  const [pending, setPending] = useState(false);
  const [documents, setDocuments] = useState(docs);
  const [statusFilter, setStatusFilter] = useState(true);
  const [mergeCompany, setMergeCompany] = useState();
  const [downloadFscTemplateTrigger, setDownloadFscTemplateTrigger] =
    useState(false);
  const statusFilterOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];
  
  let component = null;
  let fields = null;
  let fNames = null;
  let table = null;
  let endpoint = null;
  let nameField = null;
  let downloadAction = false;
  let canDelete = true;
  let doNotDisableId = null;

  useEffect(() => {
    if (url) {
      const controller = new AbortController();
      const headers = new Headers();
      headers.append("Authorization", `Bearer ${JwtToken}`);
      headers.append("Content-Type", "application/json");
      const requestOptions = {
        method: "GET",
        headers: headers,
        signal: controller.signal,
      };
      fetch(url, requestOptions)
        .then((response) => {
          if (!response.ok) {
            if (response.status === 401) {
              setAuthStatus(false);
              setAuthError("Not authorized");
              navigate("/login");
            } else {
              setError("Something went wrong🧐");
              return;
            }
          }
          return response.json();
        })
        .then((data) => {
          setDocuments(data);
          setState(data);
        })
        .catch((error) => {
          console.log(error);
        });
      return () => controller.abort();
    }
  }, [
    trigger,
    JwtToken,
    url,
    setState,
    setDocuments,
    setAuthError,
    setError,
    setAuthStatus,
    navigate,
  ]);

  switch (coll) {
    case "Companies":
      fields = collectionData.companies.fields;
      fNames = collectionData.companies.fieldNames;
      table = "account_companies_tbl";
      endpoint = "account-company";
      nameField = "company_name";
      component = (
        <AddCompany id={docId} coDocs={documents} setDocId={setDocId} />
      );
      break;
    case "Admin Companies":
      fields = collectionData.adminCompanies.fields;
      fNames = collectionData.adminCompanies.fieldNames;
      table = "company_tbl";
      endpoint = "companies";
      nameField = "company_name";
      canDelete = false;
      doNotDisableId = 1;
      component = <AddEditAdminCompany id={docId} setDocId={setDocId} />;
      break;
    case "Standard Freight Rate Sheets":
      fields = collectionData.rateSheets.fields;
      fNames = collectionData.rateSheets.fieldNames;
      table = "rate_sheets_tbl";
      endpoint = "rate-sheet";
      nameField = "rs_name";
      component = (
        <AddRateSheet id={docId} docs={documents} setDocId={setDocId} />
      );
      break;
    case "Tiered Freight Rate Sheets":
      fields = collectionData.tierdRateSheets.fields;
      fNames = collectionData.tierdRateSheets.fieldNames;
      table = "rate_sheets_tbl";
      endpoint = "rate-sheet";
      nameField = "rs_name";
      component = (
        <AddTieredRateSheet id={docId} docs={documents} setDocId={setDocId} />
      );
      break;
    case "Asset Rate Sheets":
      fields = collectionData.assetRateSheets.fields;
      fNames = collectionData.assetRateSheets.fieldNames;
      table = "asset_rate_sheets_tbl";
      endpoint = "asset-rate-sheet";
      nameField = "rs_name";
      component = (
        <AddAssetRateSheet id={docId} docs={documents} setDocId={setDocId} />
      );
      break;
    case "Fuel Surcharge":
      fields = collectionData.fscRateSheets.fields;
      fNames = collectionData.fscRateSheets.fieldNames;
      table = "fsc_tbl";
      endpoint = "fsc-rate-sheet";
      nameField = "fsc_name";
      downloadAction = true;
      component = (
        <AddFuelSurcharge id={docId} docs={documents} setDocId={setDocId} />
      );
      break;
    case "Company Carriers":
      fields = collectionData.companyCarriers.fields;
      fNames = collectionData.companyCarriers.fieldNames;
      table = "company_carriers_tbl";
      endpoint = "company-carriers";
      nameField = "carrier_name";
      component = (
        <AddEditCompanyCarrier
          docId={docId}
          setDocId={setDocId}
          setTrigger={setTrigger}
        />
      );
      break;
    case "Carrier Drivers":
      fields = collectionData.carrierDrivers.fields;
      fNames = collectionData.carrierDrivers.fieldNames;
      table = "company_drivers_tbl";
      endpoint = "carrier-driver";
      nameField = `first_name`;
      component = (
        <AddEditCarrierDriver
          docId={docId}
          setDocId={setDocId}
          setTrigger={setTrigger}
        />
      );
      break;
    case "Company Drivers":
      fields = collectionData.carrierDrivers.fields;
      fNames = collectionData.carrierDrivers.fieldNames;
      table = "company_drivers_tbl";
      endpoint = "carrier-driver";
      nameField = `first_name`;
      component = (
        <AddEditCompanyDriver
          docId={docId}
          setDocId={setDocId}
          setTrigger={setTrigger}
        />
      );
      break;
    case "Company Lists":
      fields = collectionData.companyLists.fields;
      fNames = collectionData.companyLists.fieldNames;
      table = "company_lists_tbl";
      endpoint = "company-list";
      nameField = `list_name`;
      component = (
        <AddCompanyList
          id={docId}
          setDocId={setDocId}
          setTrigger={setTrigger}
        />
      );
      break;
    default:
      break;
  }

  const handleNavigate = (id) => {
    var endpoint;
    switch (coll) {
      case "Company Carriers":
        endpoint = `/company-carriers/${id}`;
        break;
      default:
        break;
    }
    navigate(endpoint);
  };

  const handleStatus = (o) => {
    setStatusFilter(o.value);
  };

  const handleMergeCompanies = (c) => {
    setCompanyMergeTrigger(true);
    setMergeCompany(c);
  };

  const handlePermission = (e, newAlignment) => {
    var doc;
    const getDocUrl = `${process.env.REACT_APP_BACKEND}/admin/companies/${e.target.id}`;
    const statusChangeUrl = `${process.env.REACT_APP_BACKEND}/admin/company/${e.target.id}`;
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);

    const getRequestOptions = {
      method: "GET",
      headers,
      signal: controller.signal,
    };

    const changeStatusOptions = {
      method: "PATCH",
      headers,
      signal: controller.signal,
    };
    setPending(true);
    fetch(getDocUrl, getRequestOptions)
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            setAuthStatus(false);
            navigate("/loging");
            return;
          } else {
            toggleNewMessage("Could not change status");
            setPending(false);
            return;
          }
        } else {
          return res.json();
        }
      })
      .then((data) => {
        doc = data;
        if (newAlignment === "yes") {
          doc.full_access = true;
          doc.updated_by_id = currentUser.id;
        } else {
          doc.full_access = false;
          doc.updated_by_id = currentUser.id;
        }
        changeStatusOptions["body"] = JSON.stringify(doc);
      })
      .then(() => {
        if (doc) {
          fetch(statusChangeUrl, changeStatusOptions)
            .then((res) => {
              if (!res.ok) {
                if (res.status === 401) {
                  setAuthStatus(false);
                  navigate("/login");
                  return;
                } else {
                  toggleNewMessage("Could not change permission");
                  setPending(false);
                  return;
                }
              } else {
                return res.json();
              }
            })
            .then((data) => {
              setState(data);
              toggleNewMessage(null, "Permission Changed");
              setPending(false);
              return;
            });
        }
      })
      .catch((_) => {
        toggleNewMessage("Could not change permission");
      })
      .finally(() => setPending(false));
    return () => {
      controller.abort();
    };
  };

  const handleToggle = (e, newAlignment) => {
    var statusChangeUrl;
    var getDocUrl;
    var doc;
    var standardBody;

    switch (coll) {
      case "Company Carriers":
        getDocUrl = `${process.env.REACT_APP_BACKEND}/pc/company-carriers/${e.target.id}`;
        statusChangeUrl = `${process.env.REACT_APP_BACKEND}/pc/company-carrier/${e.target.id}`;
        break;
      case "Carrier Drivers":
        getDocUrl = `${process.env.REACT_APP_BACKEND}/pc/carrier-driver/${e.target.id}`;
        statusChangeUrl = `${process.env.REACT_APP_BACKEND}/pc/carrier-driver/${e.target.id}`;
        break;
      case "Company Drivers":
        getDocUrl = `${process.env.REACT_APP_BACKEND}/pc/carrier-driver/${e.target.id}`;
        statusChangeUrl = `${process.env.REACT_APP_BACKEND}/pc/carrier-driver/${e.target.id}`;
        break;
      case "Admin Companies":
        getDocUrl = `${process.env.REACT_APP_BACKEND}/admin/companies/${e.target.id}`;
        statusChangeUrl = `${process.env.REACT_APP_BACKEND}/admin/company/${e.target.id}`;
        break;
      case "Standard Freight Rate Sheets":
        standardBody = {
          id: parseInt(e.target.id),
          table: "rate_sheets_tbl",
          new_status: newAlignment === "active" ? true : false,
          is_tiered: false,
          updated_by_id: currentUser.id,
        };
        statusChangeUrl = `${process.env.REACT_APP_BACKEND}/pc/update-status`;
        break;
      case "Tiered Freight Rate Sheets":
        standardBody = {
          id: parseInt(e.target.id),
          table: "rate_sheets_tbl",
          new_status: newAlignment === "active" ? true : false,
          is_tiered: true,
          updated_by_id: currentUser.id,
        };
        statusChangeUrl = `${process.env.REACT_APP_BACKEND}/pc/update-status`;
        break;
      case "Companies":
        standardBody = {
          id: parseInt(e.target.id),
          table: "account_companies_tbl",
          new_status: newAlignment === "active" ? true : false,
          updated_by_id: currentUser.id,
        };
        statusChangeUrl = `${process.env.REACT_APP_BACKEND}/pc/update-status`;
        break;
      case "Asset Rate Sheets":
        standardBody = {
          id: parseInt(e.target.id),
          table: "asset_rate_sheets_tbl",
          new_status: newAlignment === "active" ? true : false,
          updated_by_id: currentUser.id,
        };
        statusChangeUrl = `${process.env.REACT_APP_BACKEND}/pc/update-status`;
        break;
      case "Fuel Surcharge":
        standardBody = {
          id: parseInt(e.target.id),
          table: "fsc_tbl",
          new_status: newAlignment === "active" ? true : false,
          updated_by_id: currentUser.id,
        };
        statusChangeUrl = `${process.env.REACT_APP_BACKEND}/pc/update-status`;
        break;
      default:
        break;
    }
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);

    const getRequestOptions = {
      method: "GET",
      headers,
      signal: controller.signal,
    };

    const changeStatusOptions = {
      method: "PATCH",
      headers,
      signal: controller.signal,
    };
    const standardOptions = {
      method: "PATCH",
      headers,
      signal: controller.signal,
      body: JSON.stringify(standardBody),
    };

    setPending(true);
    if (!standardBody) {
      fetch(getDocUrl, getRequestOptions)
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              setAuthStatus(false);
              navigate("/loging");
              return;
            } else {
              toggleNewMessage("Could not change status");
              setPending(false);
              return;
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          doc = data;
          if (newAlignment === "active") {
            doc.is_active = true;
            doc.updated_by_id = currentUser.id;
          } else {
            doc.is_active = false;
            doc.updated_by_id = currentUser.id;
          }
          changeStatusOptions["body"] = JSON.stringify(doc);
        })
        .then(() => {
          if (doc) {
            fetch(statusChangeUrl, changeStatusOptions)
              .then((res) => {
                if (!res.ok) {
                  if (res.status === 401) {
                    setAuthStatus(false);
                    navigate("/login");
                    return;
                  } else {
                    toggleNewMessage("Could not change status");
                    setPending(false);
                    return;
                  }
                } else {
                  return res.json();
                }
              })
              .then((data) => {
                setState(data);
                toggleNewMessage(null, "Status Changed");
                setPending(false);
                return;
              });
          }
        })
        .catch((_) => {
          toggleNewMessage("Could not change status");
        })
        .finally(() => setPending(false));
      return () => controller.abort();
    } else {
      fetch(statusChangeUrl, standardOptions)
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              setAuthStatus(false);
              navigate("/login");
              return;
            } else {
              toggleNewMessage("Could not change status");
              setPending(false);
              return Promise.resolve();
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (!data) {
            return;
          } else {
            setState(data);
            toggleNewMessage(null, "Status Changed");
            setPending(false);
            return;
          }
        })
        .catch((_) => {
          toggleNewMessage("Could not update status");
          setPending(false);
        })
        .finally(() => setPending(false));
      return () => controller.abort();
    }
  };

  const handleCompanyLogin = (companyId) => {
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    const requestOptions = {
      method: "GET",
      headers,
      signal: controller.signal,
    };

    fetch(
      `${process.env.REACT_APP_BACKEND}/admin/company-login/${companyId}`,
      requestOptions,
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          toggleNewMessage(data.error);
        } else {
          setJwtToken(data.tokens.access_token);
          setCurrentUser({
            id: data.current_user.id,
            display_name: data.current_user.display_name,
            role_name: data.current_user.role_name,
            is_approved: data.current_user.is_approved,
            company_id: data.current_user.company_id,
            first_name: data.current_user.first_name,
            last_name: data.current_user.last_name,
          });
          setCurrentCompany(data.company);
          setAuthStatus(true);
          navigate("/");
        }
      })
      .catch((error) => {
        console.log(error);
      });
    return () => controller.abort();
  };
  
  if (error) {
    return <ErrorDisplay code={500} message={"Something Went Wrong"} />;
  }
  if (pending) {
    return <Loading />;
  }
  return (
    <div>
      {!doNotDisplayTitle && (
        <div className="d-flex justify-content-between">
          <h2>{!collectionName ? coll : collectionName}</h2>
          <div className="d-flex">
            {!pending && (
              <img
                src={Add}
                alt="add"
                onClick={() => setTrigger(true)}
                className="project-delete-image"
              />
            )}
            {!pending && coll === "Fuel Surcharge" && (
              <img
                src={DownloadFscTemplate}
                alt="download"
                className="project-delete-image"
                onClick={() => setDownloadFscTemplateTrigger(true)}
              />
            )}
          </div>
        </div>
      )}
      <div className="display-list">
        {err && <p className="error">{err}</p>}
        {statusToggle && docs && (
          <DropDown
            name="is_active"
            classThree="col-sm-2"
            options={statusFilterOptions}
            value={statusFilterOptions.filter((x) => x.value === statusFilter)}
            onChange={handleStatus}
          />
        )}
        {docs ? (
          <table className="table table-sm table-hover">
            <thead>
              <tr>
                {docs &&
                  fields.map((field, index) => <th key={index}>{field}</th>)}
                {coll === "Admin Companies" && docs && (
                  <th key={"permission-toggle"}>Full Permission</th>
                )}
                {statusToggle && docs && <th key={"status-toggle"}>Status</th>}
              </tr>
            </thead>
            <tbody>
              {docs &&
                !docs.error &&
                docs.map((document) =>
                  statusToggle ? (
                    document.is_active === statusFilter && (
                      <tr key={document.id}>
                        {fNames.map((f, index) =>
                          !isNavigation ? (
                            <td key={index}>{(f === "created_at" || f === "updated_at") ? getLocalDateString(document[f]) : document[f]}</td>
                          ) : (
                            <td
                              key={index}
                              onClick={() => handleNavigate(document.id)}
                              style={{ cursor: "pointer" }}
                            >
                              {document[f]}
                            </td>
                          ),
                        )}
                        <td className="action">
                          {!pending && (
                            <div className="container">
                              <EditSelected
                                setEditIsTriggered={setTrigger}
                                setId={setDocId}
                                id={document.id}
                              />
                              {coll === "Admin Companies" && (
                                <img
                                  src={Login}
                                  alt="login"
                                  className="edit"
                                  onClick={() =>
                                    handleCompanyLogin(document.id)
                                  }
                                />
                              )}
                              {coll === "Admin Companies" &&
                                document.company_type_id === 34 && (
                                  <div className="edits">
                                    <img
                                      src={Merge}
                                      className="merge"
                                      onClick={() =>
                                        handleMergeCompanies(document)
                                      }
                                      alt="merge"
                                    />
                                  </div>
                                )}
                              {canDelete && (
                                <DeleteSelected
                                  setDeleteIsTriggered={setDeleteIsTriggered}
                                  setId={setDocId}
                                  id={document.id}
                                />
                              )}
                              {updateRequiredField &&
                                document[updateRequiredField] ===
                                  updateRequiredValue && (
                                  <UpdateRequired
                                    message={"Please update this company"}
                                    displayType={"Update"}
                                  />
                                )}
                              {downloadAction && (
                                <DownloadCsv
                                  id={document.id}
                                  endpoint={endpoint}
                                />
                              )}
                            </div>
                          )}
                        </td>
                        {coll === "Admin Companies" &&
                        !pending &&
                        document.id !== doNotDisableId &&
                        document.company_type_id !== 33 &&
                        document.company_type_id !== 34 ? (
                          <td>
                            <ToggleButtonGroup
                              value={document.full_access ? "yes" : "no"}
                              exclusive
                              onChange={handlePermission}
                              aria-label="PermissionStatus"
                              size="small"
                            >
                              <ToggleButton
                                value="yes"
                                color="success"
                                id={document.id}
                                sx={{ borderRadius: "20px" }}
                              >
                                Yes
                              </ToggleButton>
                              <ToggleButton
                                value="no"
                                color="warning"
                                id={document.id}
                                sx={{ borderRadius: "20px" }}
                              >
                                No
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </td>
                        ) : coll === "Admin Companies" &&
                          document.id !== doNotDisableId &&
                          document.company_type_id !== 33 &&
                          document.company_type_id !== 34 ? (
                          <td>
                            <ToggleButtonGroup
                              value={document.full_access ? "yes" : "no"}
                              exclusive
                              onChange={handlePermission}
                              aria-label="PermissionStatus"
                              size="small"
                              disabled
                            >
                              <ToggleButton
                                value="yes"
                                color="success"
                                id={document.id}
                                sx={{ borderRadius: "20px" }}
                              >
                                Yes
                              </ToggleButton>
                              <ToggleButton
                                value="no"
                                color="warning"
                                id={document.id}
                                sx={{ borderRadius: "20px" }}
                              >
                                No
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </td>
                        ) : (
                          coll === "Admin Companies" && <td></td>
                        )}
                        {statusToggle &&
                        docs &&
                        !pending &&
                        document.id !== doNotDisableId ? (
                          <td>
                            <ToggleButtonGroup
                              value={document.is_active ? "active" : "inactive"}
                              exclusive
                              onChange={handleToggle}
                              aria-label="ActiveStatus"
                              size="small"
                            >
                              <ToggleButton
                                value="active"
                                color="success"
                                id={document.id}
                                sx={{
                                  borderRadius: "20px",
                                  textTransform: "none",
                                }}
                              >
                                Active
                              </ToggleButton>
                              <ToggleButton
                                value="inactive"
                                color="warning"
                                id={document.id}
                                sx={{
                                  borderRadius: "20px",
                                  textTransform: "none",
                                }}
                              >
                                Inactive
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </td>
                        ) : statusToggle && document.id !== doNotDisableId ? (
                          <td>
                            <ToggleButtonGroup
                              value={document.is_active ? "active" : "inactive"}
                              exclusive
                              onChange={handleToggle}
                              aria-label="ActiveStatus"
                              size="small"
                              disabled
                            >
                              <ToggleButton
                                value="active"
                                color="success"
                                id={document.id}
                                sx={{
                                  borderRadius: "20px",
                                  textTransform: "none",
                                }}
                              >
                                Active
                              </ToggleButton>
                              <ToggleButton
                                value="inactive"
                                color="warning"
                                id={document.id}
                                sx={{
                                  borderRadius: "20px",
                                  textTransform: "none",
                                }}
                              >
                                Inactive
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </td>
                        ) : (
                          statusToggle && <td></td>
                        )}
                      </tr>
                    )
                  ) : (
                    <tr key={document.id}>
                      {fNames.map((f, index) =>
                        !isNavigation ? (
                          <td key={index}>{document[f]}</td>
                        ) : (
                          <td
                            key={index}
                            onClick={() => handleNavigate(document.id)}
                            style={{ cursor: "pointer" }}
                          >
                            {document[f]}
                          </td>
                        ),
                      )}
                      <td className="action">
                        {!pending && (
                          <div className="container">
                            <EditSelected
                              setEditIsTriggered={setTrigger}
                              setId={setDocId}
                              id={document.id}
                            />
                            {canDelete && (
                              <DeleteSelected
                                setDeleteIsTriggered={setDeleteIsTriggered}
                                setId={setDocId}
                                id={document.id}
                              />
                            )}
                            {updateRequiredField &&
                              document[updateRequiredField] ===
                                updateRequiredValue && (
                                <UpdateRequired
                                  message={"Please update this company"}
                                  displayType={"Update"}
                                />
                              )}
                            {downloadAction && (
                              <DownloadCsv
                                id={document.id}
                                endpoint={endpoint}
                              />
                            )}
                          </div>
                        )}
                      </td>
                      {coll === "Admin Companies" &&
                      !pending &&
                      document.id !== doNotDisableId ? (
                        <td>
                          <ToggleButtonGroup
                            value={document.full_access ? "yes" : "no"}
                            exclusive
                            onChange={handlePermission}
                            aria-label="PermissionStatus"
                            size="small"
                          >
                            <ToggleButton
                              value="yes"
                              color="success"
                              id={document.id}
                              sx={{ borderRadius: "20px" }}
                            >
                              Yes
                            </ToggleButton>
                            <ToggleButton
                              value="no"
                              color="warning"
                              id={document.id}
                              sx={{ borderRadius: "20px" }}
                            >
                              No
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </td>
                      ) : coll === "Admin Companies" &&
                        document.id !== doNotDisableId ? (
                        <td>
                          <ToggleButtonGroup
                            value={document.full_access ? "yes" : "no"}
                            exclusive
                            onChange={handlePermission}
                            aria-label="PermissionStatus"
                            size="small"
                            disabled
                          >
                            <ToggleButton
                              value="yes"
                              color="success"
                              id={document.id}
                              sx={{ borderRadius: "20px" }}
                            >
                              Yes
                            </ToggleButton>
                            <ToggleButton
                              value="no"
                              color="warning"
                              id={document.id}
                              sx={{ borderRadius: "20px" }}
                            >
                              No
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </td>
                      ) : (
                        coll === "Admin Companies" && <td></td>
                      )}
                      {statusToggle &&
                      !pending &&
                      document.id !== doNotDisableId ? (
                        <td>
                          <ToggleButtonGroup
                            value={document.is_active ? "active" : "inactive"}
                            exclusive
                            onChange={handleToggle}
                            aria-label="ActiveStatus"
                            size="small"
                          >
                            <ToggleButton
                              value="active"
                              color="success"
                              id={document.id}
                              sx={{
                                borderRadius: "20px",
                                textTransform: "none",
                              }}
                            >
                              Active
                            </ToggleButton>
                            <ToggleButton
                              value="inactive"
                              color="warning"
                              id={document.id}
                              sx={{
                                borderRadius: "20px",
                                textTransform: "none",
                              }}
                            >
                              Inactive
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </td>
                      ) : statusToggle && document.id !== doNotDisableId ? (
                        <td>
                          <ToggleButtonGroup
                            value={document.is_active ? "active" : "inactive"}
                            exclusive
                            onChange={handleToggle}
                            aria-label="ActiveStatus"
                            size="small"
                            disabled
                          >
                            <ToggleButton
                              value="active"
                              color="success"
                              id={document.id}
                              sx={{
                                borderRadius: "20px",
                                textTransform: "none",
                              }}
                            >
                              Active
                            </ToggleButton>
                            <ToggleButton
                              value="inactive"
                              color="warning"
                              id={document.id}
                              sx={{
                                borderRadius: "20px",
                                textTransform: "none",
                              }}
                            >
                              Inactive
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </td>
                      ) : (
                        statusToggle && <td></td>
                      )}
                    </tr>
                  ),
                )}
            </tbody>
          </table>
        ) : (
          <p className="no-docs-added">No {coll} have been added🧐</p>
        )}
        {trigger ? component : ""}
        {companyMergeTrigger && <MergeCompanies mergeCompany={mergeCompany} />}
        {deleteIsTriggered && canDelete ? (
          <DeletePopup
            setTrigger={setDeleteIsTriggered}
            id={docId}
            table={table}
            endpoint={endpoint}
            nameField={nameField}
            projectId={0}
            setDocId={setDocId}
            setState={setDeleteState}
            setStates={setDeleteStates}
            setStateFields={setDeleteStateFields}
          />
        ) : (
          ""
        )}
        {downloadFscTemplateTrigger && (
          <CreateFscTemplate
            setDownloadFscTemplateTrigger={setDownloadFscTemplateTrigger}
          />
        )}
      </div>
    </div>
  );
}
